/**
 * Verbinden per Klick und die Sätze der Karte „Vespera Desktop (Mac)“.
 * (29.09.2026, Ticket 2823)
 *
 * Die neue Mac-App (ab 1.0) versteht den Link
 * `vespera-desktop://verbinden?code=XYZ` und übernimmt den Code selbst —
 * niemand muss ihn abtippen. Die alte App (0.x, meldet keine Version) kennt
 * das nicht: für sie bleibt der Weg über das Terminal.
 *
 * ⚠️ Die App kennt NUR das Ziel `verbinden` (mac-desktop/app/vespera/logik.py,
 *    deeplink_code). Jeder andere Link landet dort als „unbekannt“ im
 *    Protokoll — darum gibt es hier keinen Knopf „Vespera Desktop öffnen“.
 */
import { istVersion, vergleicheVersion } from "../../shared/app-version.js";
import type { DaemonStatus } from "./connectors-api.js";

export const DESKTOP_SCHEMA = "vespera-desktop";

/** Der Link, der Vespera Desktop öffnet und den Code übergibt. */
export function verbindenLink(code: string): string {
  return `${DESKTOP_SCHEMA}://verbinden?code=${encodeURIComponent(code)}`;
}

/** Meldet die verbundene App eine Version ab 1.0? Dann ist es die neue. */
export function istNeueDesktopApp(version: string | null | undefined): boolean {
  return istVersion(version) && vergleicheVersion(version, "1.0.0") >= 0;
}

/**
 * Welcher Weg zum Koppeln angeboten wird.
 *
 * Neu, wenn die neue App verbunden ist — oder wenn nichts verbunden ist und
 * die neue App zum Herunterladen bereitliegt. Ist die alte App verbunden
 * (keine Version), bleibt ihr Weg.
 */
export function koppelnMitNeuerApp(s: Pick<DaemonStatus, "connected" | "version" | "download">): boolean {
  if (istNeueDesktopApp(s.version)) return true;
  return !s.connected && !!s.download;
}

/**
 * Ein Mac-Browser, nicht die App und kein iPad.
 *
 * ⚠️ Safari auf dem iPad meldet sich als „Macintosh“ — der Unterschied sind
 *    die Berührungspunkte. Ein Link, der eine Mac-App öffnen soll, hat dort
 *    kein Ziel.
 */
export function istMacBrowser(nativ: boolean, nav: Pick<Navigator, "userAgent" | "maxTouchPoints"> = navigator): boolean {
  if (nativ) return false;
  return /Macintosh|Mac OS X/.test(nav.userAgent) && (nav.maxTouchPoints ?? 0) < 2;
}

/** Der rote Hinweis, wenn die Bedienungshilfen fehlen — je nach App. */
export function bedienungshilfenHinweis(neueApp: boolean): string {
  return neueApp
    ? "Bedienungshilfen für Vespera Desktop fehlen – ohne sie kann Vespera nicht klicken und tippen."
    : "Achtung: Bedienungshilfen-Permission fehlt. Hotkey/Klick werden im macOS ignoriert. Systemeinstellungen → Datenschutz & Sicherheit → Bedienungshilfen → Python aktivieren.";
}

/** Was unter dem Code steht — wo er einzugeben ist. */
export const CODE_HINWEIS = {
  /** Neue App, am Mac nach dem Klick: der Link hat den Code schon übergeben. */
  klick: "Öffnet sich Vespera Desktop nicht von selbst, den Code dort eintippen.",
  /** Neue App, der Code wird abgetippt (anderes Gerät oder ohne Klick). */
  eintippen: "Code in Vespera Desktop am Mac eintippen.",
  /** Alte App (0.x): der Weg über das Terminal. */
  alt: "Code ins Terminal-Setup eingeben (Daemon-Installation).",
} as const;

/** Den Link öffnen — eine eigene Stelle, damit Tests ihn abfangen können. */
export function oeffneLink(adresse: string): void {
  window.location.href = adresse;
}

/**
 * Der Knopf zum Herunterladen — mit der Version, die man bekommt. Ist es nicht
 * die freigegebene (`aktuell`), sondern eine Fassung vor der Freigabe, steht
 * „Testversion“ dabei.
 */
export function downloadText(version: string | null | undefined, aktuell: string | null | undefined): string {
  if (!istVersion(version)) return "Vespera Desktop herunterladen";
  const frei = istVersion(aktuell) && vergleicheVersion(version, aktuell) === 0;
  return `Vespera Desktop ${version} herunterladen${frei ? "" : " (Testversion)"}`;
}

/**
 * Ehrlich zu den macOS-Freigaben: Solange die App nicht von Apple signiert
 * ist, öffnet sie sich nur per Rechtsklick, und macOS hält jede neue Version
 * für eine andere App (desktop/README.md, „Ehrlich zu den Freigaben“).
 */
export const DOWNLOAD_HINWEIS =
  "Noch ohne Apple-Signatur: beim ersten Öffnen sagt macOS, die App sei nicht geprüft („nicht geöffnet“, dann „Fertig“ wählen). Dann Systemeinstellungen → Datenschutz & Sicherheit → ganz unten bei „Vespera Desktop“ auf „Trotzdem öffnen“ (Rechtsklick → Öffnen geht ab macOS 15 nicht mehr). Danach Bildschirmaufnahme und Bedienungshilfen einmal in den Systemeinstellungen erlauben — nach einem Update fragt macOS erneut.";

/** Vorweg bei einer Testversion: „Nach Updates suchen“ in der App kennt sie nicht (sie steht nicht im Update-Weg). */
const TESTVERSION_HINWEIS =
  "Das ist eine Testversion. „Nach Updates suchen“ in der App kennt sie nicht und meldet deshalb „neuester Stand“. Bitte von Hand installieren.";

/** Der Absatz unter dem Knopf: bei einer Testversion mit dem Satz oben davor. */
export function downloadHinweis(version: string | null | undefined, aktuell: string | null | undefined): string {
  const frei = !istVersion(version) || (istVersion(aktuell) && vergleicheVersion(version, aktuell) === 0);
  return frei ? DOWNLOAD_HINWEIS : `${TESTVERSION_HINWEIS} ${DOWNLOAD_HINWEIS}`;
}

const UHR_WIEN = new Intl.DateTimeFormat("de-AT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Vienna" });
const TAG_WIEN = new Intl.DateTimeFormat("de-AT", { day: "2-digit", month: "2-digit", timeZone: "Europe/Vienna" });
const ISO_ZEIT = /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}(:[0-9]{2}(\.[0-9]{1,9})?)?(Z|[+-][0-9]{2}:[0-9]{2})$/;
/** Der Kalendertag in Wien („2026-10-07“) — nur zum Vergleichen „heute oder nicht“. */
const KALENDER_WIEN = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "Europe/Vienna" });

/**
 * Eine Zeit in Wiener Zeit: „17:33“ (heute in Wien) oder „05.10. 17:33“. Leer, wenn die Angabe fehlt oder keine
 * Zeit ist. (07.10.2026) Wiener Zeit, egal wo das Gerät steht — Martin liest sie in Sooß; ein Browser in einer
 * anderen Zone zeigte vorher seine eigene Uhr.
 */
export function zeitWien(iso: string | null | undefined, jetzt: Date = new Date()): string {
  // Nur echte ISO-Zeiten (der Server schickt toISOString): `new Date("1")` wäre sonst der 1. 1. 2001, 00:00.
  if (!iso || !ISO_ZEIT.test(iso)) return "";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "";
  const heute = KALENDER_WIEN.format(t) === KALENDER_WIEN.format(jetzt);
  return heute ? UHR_WIEN.format(t) : `${TAG_WIEN.format(t)} ${UHR_WIEN.format(t)}`;
}

/**
 * Ein zweites Vespera Desktop meldet sich als ein Mac an, der schon verbunden ist. (07.10.2026)
 *
 * Martin: „Es gibt keine stabile Verbindung zu Vespera Desktop“ — zwei Programme mit demselben Konto warfen sich
 * gegenseitig hinaus.
 *
 * ⚠️ Seit dem Mehr-Geräte-Betrieb (Plan „mehrere Geräte“, S1) bleiben zwei VERSCHIEDENE Macs nebeneinander
 *    verbunden. `zweiteVersuche` zählt nur noch Versuche DESSELBEN Geräts (server/daemon-bridge.ts zweiteZaehlen:
 *    gleiche Gerätekennung bzw. — ältere App — gleicher Schlüssel). Das heißt: das Programm läuft auf einem Mac
 *    doppelt, oder zwei Macs teilen sich eine Kopplung (kopierte Einstellungen). Der alte Satz „Beende Vespera
 *    Desktop auf dem Mac, den du gerade nicht brauchst“ log jetzt: Für einen zweiten Mac ist Neu-Koppeln der Weg,
 *    dann laufen beide.
 *
 * Leer, wenn nichts zu sagen ist: nicht verbunden, kein Versuch, oder unsaubere Angaben.
 */
export function zweitesProgrammText(s: Pick<DaemonStatus, "connected" | "zweiteVersuche">): string {
  const t = zweitesProgrammTeile(s);
  return t ? `${t.vor}${t.zahl}${t.nach}` : "";
}

/**
 * Derselbe Satz in drei Teilen: `zahl` („ (12×, zuletzt 17:03)“) ändert sich bei jedem Abruf, weil die App es alle
 * paar Sekunden wieder versucht. Die Karte legt sie in einen für Vorleseprogramme stillen Teil — sonst sagte die
 * Meldung (role=status) alle 10 s dasselbe an (Prüfung 07.10.). `vor` + `nach` ist, was vorgelesen wird.
 */
export function zweitesProgrammTeile(s: Pick<DaemonStatus, "connected" | "zweiteVersuche">): { vor: string; zahl: string; nach: string } | null {
  const v = s.zweiteVersuche;
  if (!s.connected || !v || !Number.isSafeInteger(v.anzahl) || v.anzahl < 1 || typeof v.zuletztIso !== "string") return null;
  const zuletzt = new Date(v.zuletztIso);
  if (Number.isNaN(zuletzt.getTime())) return null;
  return {
    vor: "Ein zweites Vespera Desktop meldet sich als ein Mac an, der schon verbunden ist, und wird abgewiesen",
    zahl: ` (${v.anzahl}×, zuletzt ${UHR_WIEN.format(zuletzt)})`,
    nach: ". Läuft es auf einem Mac doppelt, beende eines. Ist es ein anderer Mac, verbinde ihn neu (unten „Weiteren Mac verbinden“) – dann laufen beide nebeneinander.",
  };
}

/**
 * „seit 17:33“ (heute) oder „seit 29.09. 17:33“ — aus der Zeit der letzten
 * Anmeldung der App, in Wiener Zeit. Leer, wenn die Angabe fehlt oder keine Zeit ist.
 */
export function seitText(iso: string | null | undefined, jetzt: Date = new Date()): string {
  const zeit = zeitWien(iso, jetzt);
  return zeit ? `seit ${zeit}` : "";
}
